import React from 'react';
import { buildRadioLiveCapsuleMessage } from './radioLiveCapsuleMessages';
import RadioListenerPresenceRow, {
  getRadioListenerPresenceCount,
} from './RadioListenerPresenceRow';
import RadioMusicVisual from './RadioMusicVisual';
import { buildRadioAudioIntensityStyle } from './useRadioAudioLevels';

export default function RadioLiveCapsule({
  capsule = null,
  activeQueue = [],
  currentIndex = 0,
  isPlaying = false,
  signals = null,
  liveSignals = null,
  transientFeedbackSignal = null,
  transientNewInRotationSignal = null,
  transientProofSummary = null,
  audioLevels = [],
  audioIntensity = 0,
  isAudioReactive = false,
  onExpand,
  returnToRadio = false,
}) {
  const message = buildRadioLiveCapsuleMessage({
    currentCapsule: capsule,
    queue: activeQueue,
    currentIndex,
    isPlaying,
    signals,
    transientFeedbackSignal,
    transientNewInRotationSignal,
    transientProofSummary,
  });
  const canExpand = returnToRadio || Boolean(capsule?.canPlay);
  const linkedArtistPresence = liveSignals?.linkedArtistPresence || {};
  const artistPresenceClass = linkedArtistPresence.isInRoom
    ? 'is-in-room'
    : linkedArtistPresence.isOnline
      ? 'is-online'
      : '';
  const artistPresenceLabel = linkedArtistPresence.isInRoom
    ? 'In JG Radio now'
    : linkedArtistPresence.isOnline
      ? 'Online in JG Universe'
      : '';
  const listenerPresence = liveSignals?.listenerPresence || null;
  const stationListenerPresence = liveSignals?.stationListenerPresence || null;
  const activeListenerCount =
    getRadioListenerPresenceCount(listenerPresence) +
    getRadioListenerPresenceCount(stationListenerPresence);
  const hasLiveMovement = Boolean(liveSignals?.movementText);
  const hasRecentMovementPayload = Boolean(
    signals?.recentMovement ||
      signals?.data?.recentMovement ||
      signals?.data?.data?.recentMovement,
  );
  const hasPriorityCapsuleMessage = Boolean(
    message.programmingMessage ||
      message.feedbackSignal ||
      message.newInRotationSignal ||
      message.proofSignal,
  );
  const showIdleMovement =
    !hasLiveMovement &&
    activeListenerCount <= 0 &&
    hasRecentMovementPayload &&
    !hasPriorityCapsuleMessage &&
    Boolean(message.recentMovementSummary?.headline);
  const audioIntensityStyle = buildRadioAudioIntensityStyle(audioIntensity);

  const artwork = capsule?.coverArtUrl ? (
    <img src={capsule.coverArtUrl} alt="" width="44" height="44" decoding="async" />
  ) : (
    <span className="jg-radio-live-capsule-mark" aria-hidden="true">
      JG
    </span>
  );

  const content = (
    <>
      <span
        className={[
          'jg-radio-live-capsule-art',
          isAudioReactive ? 'is-audio-reactive' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={audioIntensityStyle}
      >
        {artwork}
      </span>
      <span className="jg-radio-live-capsule-copy">
        <span className="jg-radio-live-capsule-topline">
          <strong>{message.brand}</strong>
          {message.liveLabel ? (
            <span
              className={[
                'jg-radio-live-capsule-live',
                message.isPlaying ? 'is-playing' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <RadioMusicVisual
                as="span"
                className="jg-radio-live-capsule-live-bars"
                variant="live"
                isPlaying={message.isPlaying}
                barCount={4}
                levels={audioLevels}
                intensity={audioIntensity}
                isBeatReactive={isAudioReactive}
              />
              {message.liveLabel}
            </span>
          ) : null}
        </span>
        <span className="jg-radio-live-capsule-title">
          <span>{message.status}</span>
          {message.title}
        </span>
        {message.artistName ? (
          <span className="jg-radio-live-capsule-artist">
            {artistPresenceClass ? (
              <span
                className={[
                  'jg-radio-live-capsule-presence-dot',
                  artistPresenceClass,
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-label={artistPresenceLabel}
                title={artistPresenceLabel}
                role="img"
              />
            ) : null}
            {message.artistName}
          </span>
        ) : null}
        {liveSignals?.movementText ? (
          <span className="jg-radio-live-capsule-movement" role="status">
            {liveSignals.movementText}
          </span>
        ) : null}
        {showIdleMovement ? (
          <span className="jg-radio-live-capsule-recent" role="status">
            {message.recentMovementSummary.headline}
          </span>
        ) : null}
        <RadioListenerPresenceRow
          presence={listenerPresence}
          label="listening to this record"
        />
        {!showIdleMovement && message.secondaryLabel ? (
          <span className="jg-radio-live-capsule-signal">
            <span>{message.secondaryLabel}</span>
            {message.secondaryTitle ? (
              <strong>
                {message.secondaryTitle}
                {message.secondaryArtistName
                  ? ` - ${message.secondaryArtistName}`
                  : ''}
              </strong>
            ) : null}
          </span>
        ) : null}
      </span>
    </>
  );

  if (!canExpand) {
    return (
      <div
        className={[
          'jg-radio-live-capsule',
          'is-disabled',
          isAudioReactive ? 'is-audio-reactive' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={audioIntensityStyle}
        role="status"
        aria-label="Joe Galaxy Radio is getting ready"
      >
        {content}
      </div>
    );
  }

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onExpand?.();
  };

  return (
    <button
      type="button"
      className={[
        'jg-radio-live-capsule',
        isAudioReactive ? 'is-audio-reactive' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={audioIntensityStyle}
      onClick={onExpand}
      onKeyDown={handleKeyDown}
      aria-label={returnToRadio ? 'Return to Radio' : 'Open Joe Galaxy Radio dock'}
    >
      {content}
    </button>
  );
}
